import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import DialogView from './DialogView';
import { useTranslation } from './LocalizationProvider';
import { useGlobalStyles } from '../util/classes';
import { loadingActions } from '../../store';

const useStyles = makeStyles((theme) => ({
  pendingCell: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
  },
  pendingLabel: {
    fontStyle: 'italic',
    fontWeight: 500,
  },
}));

const RecoveryDialog = ({ deviceId, open, handleClose }) => {
  const t = useTranslation();
  const classes = useGlobalStyles();
  const localClasses = useStyles();
  const dispatch = useDispatch();

  const [users, setUsers] = useState();

  const validate = () => true;

  const setAssignment = async (allUsers, assignedUsers) => {
    // Create a set of assigned user IDs for quick lookup
    const assignedUserIds = new Set(assignedUsers.map((user) => user.id));
    // Mark users as assigned if they're in the assigned list
    allUsers.forEach((user) => {
      if (assignedUserIds.has(user.id)) {
        user.origAssigned = true;
        user.newAssigned = true;
      } else {
        user.origAssigned = false;
        user.newAssigned = false;
      }
    });

    // Sort once at load: originally-assigned rows on top, then alphabetical
    // within each group. Sort key is origAssigned so order stays stable as
    // the user ticks/unticks boxes — pending changes are shown via styling.
    allUsers.sort((a, b) => {
      if (a.origAssigned !== b.origAssigned) return a.origAssigned ? -1 : 1;
      return (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
    });

    setUsers(allUsers);
  };

  useEffect(() => {
    async function fetchData() {
      if (open) {
        dispatch(loadingActions.startRequest());
        try {
          // Fetch all recovery users and assigned recovery users in parallel
          const [allUsersResponse, assignedUsersResponse] = await Promise.all([
            fetch('/api/users?recovery=1'),
            fetch(`/api/devices/${deviceId}/recovery`),
          ]);

          const allUsers = await allUsersResponse.json();
          const assignedUsers = await assignedUsersResponse.json();
          await setAssignment(allUsers, assignedUsers);
        } catch (error) {
          // Silent error handling
        } finally {
          dispatch(loadingActions.finishRequest());
        }
      }
    }
    fetchData();
  }, [deviceId, open, dispatch]);

  const handleOnChange = (index) => {
    users[index].newAssigned = !users[index].newAssigned;
    setUsers([...users]);
  };

  const hasChanges = !!users && users.some((user) => user.newAssigned !== user.origAssigned);

  return (
    <DialogView
      item={users}
      open={open}
      handleClose={handleClose}
      dialogTitle={t('sharedRecovery')}
      validate={validate}
      acceptTitle={t('sharedSave')}
      acceptDisabled={!hasChanges}
      content={(
        <Table size="small">
          <TableBody>
            {!users && (
              <TableRow>
                <TableCell className={classes.cell} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {users && users.map((user, index) => {
              const pending = user.newAssigned !== user.origAssigned;
              return (
                <TableRow key={user.id}>
                  <TableCell className={`${classes.cell} ${pending ? localClasses.pendingCell : ''}`}>
                    <FormGroup>
                      <FormControlLabel
                        control={<Checkbox checked={user.newAssigned} onChange={() => handleOnChange(index)} />}
                        label={user.name}
                        componentsProps={pending ? { typography: { className: localClasses.pendingLabel } } : undefined}
                      />
                    </FormGroup>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    />
  );
};

export default RecoveryDialog;
